import * as React from 'react';
import { useEffect, useState } from 'react';
import { CenterFormWidget } from '../../widgets/layout/CenterFormWidget';
import { ButtonType, ButtonWidget } from '../../widgets/buttons/ButtonWidget';
import { FormLayoutWidget } from '../../widgets/layout/FormLayoutWidget';
import { FormikOTPWidget } from '../../widgets/inputs';
import { styled } from '../../theme';
import { Keys, useKeyPress } from '../../hooks/useKeyPress';
import { FormikContext, useFormik } from 'formik';
import { ParagraphWidget } from '../../widgets/forms/ParagraphWidget';
import { SignupPayload } from './SignupPage';
import { useSystem } from '../../hooks/useSystem';
import { useFlightLogger } from '../../hooks/useFlightLogger';
import { SignupActionSource } from '@journeyapps-platform/types-flight-log';
import { GetSignupUserResponse } from '@journeyapps-platform/types-accounts-hub';

export interface SignupStep3WidgetProps {
  back: () => any;
  next: () => any;
  user: GetSignupUserResponse;
  payload: Partial<SignupPayload>;
}

type OTPValues = {
  otp: string[];
};

const initialValue: string[] = ['', '', '', ''];
const WAIT_FOR_RETRY = 5000; //ms
export const SignupStep3Widget: React.FC<SignupStep3WidgetProps> = (props) => {
  const [failed, setFailed] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showRetry, setShowRetry] = useState<boolean>(false);
  const system = useSystem();
  const flightLogger = useFlightLogger({ stepId: 'signup-step3', userId: props.user.id });

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowRetry(true);
    }, WAIT_FOR_RETRY);
    return () => clearTimeout(timer);
  }, []);

  const resend = async () => {
    if (!props.payload.mobile) {
      return;
    }
    try {
      setSubmitting(true);
      await system.sessionAccountsPortalClient.sendOTP({
        phone_number: props.payload.mobile,
        token: system.tokenStore.token || ''
      });
    } catch (ex) {
      console.error('Failed to resend code', ex);
      flightLogger.logAction({ source: SignupActionSource.SYSTEM, action_id: 'failed-resend-otp' });
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async (value: string) => {
    setSubmitting(true);
    setFailed(false);
    try {
      await system.sessionAccountsPortalClient.verifyOTP({
        code: value
      });
      props.next();
    } catch (ex) {
      flightLogger.logAction({ source: SignupActionSource.SYSTEM, action_id: 'failed-otp-validation' });
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik<OTPValues>({
    initialValues: {
      otp: initialValue
    },
    validate: (values) => {
      if (!values.otp) {
        return { otp: 'This is a required field' };
      }
    },
    onSubmit: (values) => {
      submit(values.otp.join(''));
    }
  });

  const clearOTP = () => {
    formik.setFieldValue('otp', initialValue, false);
  };

  useKeyPress(Keys.ENTER, {
    onKeyPressed: async () => {
      setSubmitting(true);
      flightLogger.logAction({ source: SignupActionSource.INPUT, action_id: 'submit-via-enter' });
      formik.submitForm();
    }
  });

  return (
    <FormikContext.Provider value={formik}>
      <CenterFormWidget primaryLabel="Secure your account using 2FA" secondaryLabel="Step 2">
        <FormLayoutWidget
          sections={[
            {
              key: 'mobile',
              elements: [
                <ParagraphWidget>{`We just sent a text message with your security code to ${props.payload.mobile}.\nEnter the code below`}</ParagraphWidget>,
                <S.StyledOTPInput
                  name={'otp'}
                  length={4}
                  autoFocus={true}
                  error={failed ? 'Incorrect code. Please try again.' : null}
                />
              ]
            },
            {
              key: 'submit',
              elements: [
                <S.Buttons>
                  <S.Btn
                    label="Back"
                    type={ButtonType.SECONDARY}
                    action={() => {
                      flightLogger.logAction({ source: SignupActionSource.CLICK, action_id: 'back-button' });
                      props.back();
                    }}
                  />
                  <S.Btn
                    label="Finish"
                    loadingOverride={submitting}
                    disabled={!formik.values.otp || formik.values.otp?.length < 4}
                    action={async (event, loading) => {
                      flightLogger.logAction({ source: SignupActionSource.CLICK, action_id: 'submit-button' });
                      loading(true);
                      await submit(formik.values.otp.join(''));
                      loading(false);
                    }}
                  />
                </S.Buttons>,
                <S.ResendBox>
                  {showRetry && (
                    <>
                      <S.ResendText>Didn't get it?</S.ResendText>
                      <S.Resend
                        onClick={async () => {
                          clearOTP();
                          flightLogger.logAction({ source: SignupActionSource.CLICK, action_id: 'resend-otp-button' });
                          await resend();
                          setShowRetry(false);
                        }}
                      >
                        Resend
                      </S.Resend>
                    </>
                  )}
                </S.ResendBox>
              ]
            }
          ]}
        />
      </CenterFormWidget>
    </FormikContext.Provider>
  );
};

namespace S {
  export const Buttons = styled.div`
    display: flex;
  `;

  export const Btn = styled(ButtonWidget)`
    width: 50%;
    margin-right: 16px;

    &:last-of-type {
      margin-right: 0;
    }
  `;

  export const StyledOTPInput = styled(FormikOTPWidget)`
    min-width: 300px;
  `;

  export const ResendBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: ${(p) => p.theme.text.primary};
    margin-top: 8px;
  `;

  export const Resend = styled.div`
    font-weight: bold;
    cursor: pointer;
  `;

  export const ResendText = styled.div``;
}
